/**
 * Quill 2 already ships a `table` format and a clipboard matcher that turns pasted
 * `<tr>`/`<td>` into it — but `th` is missing from Quill's block-element list, so
 * nothing closes the line after a header cell and every `th` in a pasted table
 * collapses into a single cell. Chat replies, Excel and Word all paste a header row,
 * so without this matcher the first row of every pasted table is mangled.
 *
 * Pass as `modules.clipboard.matchers`; the `table` format also has to be in the
 * editor's `formats` whitelist or Quill drops the rows entirely.
 */

/**
 * The slice of quill-delta this needs. Declared structurally rather than imported so
 * the matcher works against whichever quill-delta copy the host Quill was built with.
 */
interface DeltaLike {
  ops: { insert?: unknown; attributes?: Record<string, unknown> }[];
  insert(text: string): DeltaLike;
}

function endsWithNewline(delta: DeltaLike) {
  const last = delta.ops[delta.ops.length - 1];
  return typeof last?.insert === "string" && last.insert.endsWith("\n");
}

/**
 * Quill has no `th` blot, so a header cell becomes an ordinary cell — bolded, so the
 * header row still reads as a header once the value is published.
 *
 * Mutates the delta in place, which is how Quill's own `matchNewline` works.
 */
function matchHeaderCell(_node: Node, delta: DeltaLike) {
  delta.ops.forEach(op => {
    if (typeof op.insert === "string") {
      op.attributes = { bold: true, ...op.attributes };
    }
  });
  if (delta.ops.length && !endsWithNewline(delta)) delta.insert("\n");
  return delta;
}

/**
 * Chat tables are rendered with a shaded header row and zebra striping (see
 * MarkdownContent in ../Chat/ChatMarkdown). The browser inlines those computed colors
 * onto the copied cells, and Quill's own `matchStyles` turns them into `background`
 * formats — so the table lands in the field covered in highlight. Cell fill belongs to
 * the destination field, not to the styling of wherever the table was copied from.
 */
function stripCellFill(_node: Node, delta: DeltaLike) {
  delta.ops.forEach(op => {
    if (!op.attributes) return;
    delete op.attributes.background;
    // An empty attributes object would still read as "formatted" downstream.
    if (!Object.keys(op.attributes).length) delete op.attributes;
  });
  return delta;
}

// Order matters: `th` gets matchHeaderCell (which adds bold) before stripCellFill, so
// the header's bold survives while its shading does not.
export const TABLE_CLIPBOARD_MATCHERS: [
  string,
  (node: Node, delta: DeltaLike) => DeltaLike,
][] = [
  ["th", matchHeaderCell],
  ["td, th", stripCellFill],
];
